// =============================================================================
// services/secure-store.ts — bare-RN secure key/value store (keychain-backed).
//
// Blueprint §1 / #7: the bare-RN replacement for expo-secure-store. SecureStore
// exposed a flat async key/value API (getItemAsync/setItemAsync/deleteItemAsync);
// react-native-keychain is a (username,password){service} credential store, NOT
// a key/value store. We bridge the two by mapping ONE logical key → ONE keychain
// `service` slot, storing the value as the `password` (the `username` is a fixed
// sentinel we don't read back).
//
// This adapter exposes the EXACT SecureStore method names + signatures so every
// caller that did `SecureStore.getItemAsync(KEY)` ports by changing only the
// import. Each export is crash-safe and resolves (never throws) — a keychain miss
// or a hardware error reads as "no value" rather than breaking auth.
//
// NB (blueprint Risk #8): keychain is a DIFFERENT physical store than SecureStore,
// so an existing logged-in user on the old Expo build won't carry their token into
// this new binary — they re-login once on first launch. Acceptable for a new app.
// =============================================================================
import * as Keychain from 'react-native-keychain';

// Fixed account/username sentinel: keychain requires a username, but we only ever
// need the value (password). We namespace the SERVICE per logical key instead.
const ACCOUNT = 'cryb';

// Keychain `service` names must be stable + collision-free. We prefix the logical
// key so two SecureStore keys can never share a slot.
function serviceFor(key: string): string {
  return `ai.cryb.secure.${key}`;
}

// In-memory mirror of every value we've written/read this process. WHY: the iOS
// keychain read can transiently return false/throw (notably on the SIMULATOR with
// an adhoc-signed, entitlement-less build, but also occasionally on device). Without
// a mirror, one flaky read makes getToken() return null → api() sees "no token" →
// it clears the session and ejects the user mid-use ("logged me out after a bit",
// most visibly on the first hard-authed screen like Profile). The mirror keeps the
// live session resilient: a keychain miss falls back to the value we already hold.
// Process-lifetime only (cleared on cold start → we then read from the persistent
// keychain), and no less secure than the token already sitting in the JS heap.
const memCache = new Map<string, string>();

/** SecureStore.getItemAsync — returns the stored string, or null when absent. */
export async function getItemAsync(key: string): Promise<string | null> {
  try {
    const creds = await Keychain.getGenericPassword({ service: serviceFor(key) });
    // getGenericPassword resolves `false` when nothing is stored for the service.
    if (creds && typeof creds.password === 'string') {
      memCache.set(key, creds.password);
      return creds.password;
    }
    // Keychain reported "nothing stored" — but if we WROTE a value this session,
    // trust the mirror over a (possibly flaky) empty read. A real logout calls
    // deleteItemAsync, which clears the mirror, so this never resurrects a
    // deliberately-cleared token.
    const cached = memCache.get(key);
    return cached ?? null;
  } catch {
    // Hard keychain error → fall back to the mirror rather than nuking the session.
    return memCache.get(key) ?? null;
  }
}

/** SecureStore.setItemAsync — persist a string value under `key`. */
export async function setItemAsync(key: string, value: string): Promise<void> {
  memCache.set(key, value); // mirror first so a failed keychain write still holds
  try {
    await Keychain.setGenericPassword(ACCOUNT, value, { service: serviceFor(key) });
  } catch {
    // non-fatal: the in-memory mirror carries the value for this session.
  }
}

/** SecureStore.deleteItemAsync — remove the value stored under `key`. */
export async function deleteItemAsync(key: string): Promise<void> {
  memCache.delete(key); // clear the mirror so a cleared token can't be resurrected
  try {
    await Keychain.resetGenericPassword({ service: serviceFor(key) });
  } catch {
    // non-fatal
  }
}

// Default export mirrors `import * as SecureStore` ergonomics so a call site can
// also do `import SecureStore from './secure-store'` if it prefers.
export default { getItemAsync, setItemAsync, deleteItemAsync };
